import React, { ReactElement } from 'react';
import { pipe } from 'fp-ts/lib/function';
import { FieldValues, UseFormSetValue } from 'react-hook-form';

import i18n from 'asset/translation';
import Widget, { WidgetType } from 'domain/entity/widget';
import { WidgetDataTypes } from 'domain/entity/widgetData';
import ChartData from 'domain/entity/widgets/ChartData';
import GaugeData from 'domain/entity/widgets/GaugeData';
import OptionsFormField, { OptionFieldKey } from 'domain/entity/options/optionFormField';
import { SELECTOR_SHOW_AS, SelectorPresetFormField, TextPresetFormField } from 'domain/entity/preset/form/presetFormField';
import { FieldContainer } from 'adapter/in/common/form/renderer';
import ChartLimitContainer, {
    getCustomChartLimit,
    mapChartChartLimitFieldData,
} from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options/ChartLimit';
import { OptionContainer } from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options/Group';
import MinMaxContainer, {
    getCustomMax,
    getCustomMin,
} from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options/MinMax';
import RangeContainer, {
    getCustomRanges,
    mapCustomRangeFieldData,
} from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options/Range';
import { ABSOLUTE_PERCENTAGE_VALUES, ALIGN_TEXT, OPTIONS } from 'adapter/in/common/widget/options';

export type OptionFieldContainer = FieldContainer;

const getAbsolutePercentageOption = () =>
    new SelectorPresetFormField(`${OPTIONS.ABSOLUTE_PERCENTAGE}`, '', [], SELECTOR_SHOW_AS.TOGGLE, 0, '', [
        {
            id: `${ABSOLUTE_PERCENTAGE_VALUES.PERCENTAGE}`,
            label: i18n.t('widgets.widgetMenu.comparison.percentage'),
            selected: true,
            properties: { shortLabel: '', icon: '' },
        },
        {
            id: `${ABSOLUTE_PERCENTAGE_VALUES.ABSOLUTE}`,
            label: i18n.t('widgets.widgetMenu.comparison.absolute'),
            selected: false,
            properties: { shortLabel: '', icon: '' },
        },
    ]);

export const getOptionsFormByWidgetType = (widgetType: WidgetType): OptionsFormField[] => {
    switch (widgetType) {
        case WidgetType.GAUGE:
            return [
                {
                    label: { text: i18n.t('widgets.widgetMenu.customTitle') },
                    fields: [CUSTOM_TITLE_OPTION],
                    key: OptionFieldKey.CUSTOM_TITLE,
                },
                {
                    label: { text: i18n.t('widgets.widgetMenu.comparison.title') },
                    fields: [getAbsolutePercentageOption()],
                    key: OptionFieldKey.COMPARISON_DISPLAY_MODE,
                },
                {
                    label: { text: i18n.t('widgets.widgetMenu.minMax.title') },
                    fields: [],
                    key: OptionFieldKey.GAUGE_MIN_MAX,
                },
                {
                    label: { text: i18n.t('widgets.widgetMenu.ranges.title') },
                    fields: [],
                    key: OptionFieldKey.GAUGE_RANGE,
                },
                {
                    label: { text: i18n.t('widgets.widgetMenu.checkpoints.title') },
                    fields: [],
                    key: OptionFieldKey.CHART_LIMIT,
                },
            ];

        case WidgetType.SINGLE_KPI:
            return [
                {
                    label: { text: i18n.t('widgets.widgetMenu.customTitle') },
                    fields: [CUSTOM_TITLE_OPTION],
                    key: OptionFieldKey.CUSTOM_TITLE,
                },
                {
                    label: { text: i18n.t('widgets.widgetMenu.comparison.title') },
                    fields: [getAbsolutePercentageOption()],
                    key: OptionFieldKey.COMPARISON_DISPLAY_MODE,
                },
            ];
        case WidgetType.BAR:
        case WidgetType.LINE:
        case WidgetType.STACKED_BAR:
            return [
                {
                    label: { text: i18n.t('widgets.widgetMenu.customTitle') },
                    fields: [CUSTOM_TITLE_OPTION],
                    key: OptionFieldKey.CUSTOM_TITLE,
                },
                {
                    label: { text: i18n.t('widgets.widgetMenu.checkpoints.title') },
                    fields: [],
                    key: OptionFieldKey.CHART_LIMIT,
                },
            ];
        case WidgetType.PIE:
        case WidgetType.HEATMAP:
        case WidgetType.MULTI_DEVICE_HEATMAP:
        case WidgetType.TABLE:
            return [
                {
                    label: { text: i18n.t('widgets.widgetMenu.customTitle') },
                    fields: [CUSTOM_TITLE_OPTION],
                    key: OptionFieldKey.CUSTOM_TITLE,
                },
            ];
        case WidgetType.TEXT:
            return [
                {
                    label: { text: i18n.t('widgets.widgetMenu.alignText.title') },
                    fields: [CUSTOM_ALIGN_TEXT_OPTION('header'), CUSTOM_ALIGN_TEXT_OPTION('body')],
                    key: OptionFieldKey.TEXT_ALIGNMENT,
                },
            ];
        case WidgetType.IMAGE:
        default:
            return [];
    }
};

export const validateWidgetIdAndGetOptionsContainers = (
    optionsForm: OptionsFormField[],
    data: WidgetDataTypes,
    widgetId: string,
    prevWidgetId?: string
): ReactElement[] => {
    // Necessary If to avoid sync issues with data
    if (widgetId !== prevWidgetId) {
        return [];
    }
    return getOptionsContainers(optionsForm, data);
};

export const getOptionsContainers = (optionsForms: OptionsFormField[], data: WidgetDataTypes): ReactElement[] =>
    optionsForms.map((form, index) => {
        switch (form.key) {
            case OptionFieldKey.GAUGE_RANGE:
                return <RangeContainer key={form.key} data={data as GaugeData} title={form.label.text} />;
            case OptionFieldKey.GAUGE_MIN_MAX:
                return <MinMaxContainer key={form.key} data={data as GaugeData} title={form.label.text} />;
            case OptionFieldKey.CHART_LIMIT:
                return <ChartLimitContainer key={form.key} title={form.label.text} />;
            default:
                return <OptionContainer key={form.key} index={index} optionsFormFields={form} data={data} />;
        }
    });

export const populateCustomOptionsCharts = (
    widget: Widget,
    setValue: UseFormSetValue<FieldValues>,
    widgetData?: ChartData | GaugeData
): void => {
    pipe(getCustomChartLimit(widget, widgetData), mapChartChartLimitFieldData, (values) => setValue(OPTIONS.CHECKPOINTS, values));
};

// Please note that if user has switched between widget types, widget type for Widget and actual widget Data will not match, hence this check function.
const isWidgetGaugeAndDataMatches = (widget: Widget, widgetData?: WidgetDataTypes): widgetData is GaugeData =>
    widget.type === WidgetType.GAUGE;

export const populateCustomOptionsGauge = (widget: Widget, setValue: UseFormSetValue<FieldValues>, widgetData?: WidgetDataTypes): void => {
    if (isWidgetGaugeAndDataMatches(widget, widgetData)) {
        populateGaugeOptions(widget, widgetData, setValue);
    } else {
        populateGaugeOptionsWithDefaults(widget, setValue);
    }
};

const populateGaugeOptions = (widget: Widget, widgetData: GaugeData, setValue: UseFormSetValue<FieldValues>) => {
    pipe(getCustomRanges(widget, widgetData), mapCustomRangeFieldData, (values) => {
        setValue(OPTIONS.RANGES, values);
    });
    populateCustomOptionsCharts(widget, setValue, widgetData);
    pipe(getCustomMin(widget, widgetData), (min) => setValue(OPTIONS.MIN, min));
    pipe(getCustomMax(widget, widgetData), (max) => setValue(OPTIONS.MAX, max));
};

const populateGaugeOptionsWithDefaults = (widget: Widget, setValue: UseFormSetValue<FieldValues>) => {
    pipe(getCustomMax(widget), (max) => setValue(OPTIONS.MAX, max));
    pipe(getCustomMin(widget), (min) => setValue(OPTIONS.MIN, min));
    pipe(getCustomRanges(widget), mapCustomRangeFieldData, (values) => {
        setValue(OPTIONS.RANGES, values);
    });
    populateCustomOptionsCharts(widget, setValue);
};

export const CUSTOM_TITLE_OPTION = new TextPresetFormField(OPTIONS.CUSTOM_TITLE, '', [], '', 280);

export const ALIGN_TEXT_OPTIONS = [
    {
        id: `${ALIGN_TEXT.START}`,
        label: i18n.t('widgets.widgetMenu.alignText.start'),
        selected: true,
        properties: { shortLabel: '', icon: 'align_text_start' },
    },
    {
        id: `${ALIGN_TEXT.CENTER}`,
        label: i18n.t('widgets.widgetMenu.alignText.center'),
        selected: false,
        properties: { shortLabel: '', icon: 'align_text_center' },
    },
    {
        id: `${ALIGN_TEXT.END}`,
        label: i18n.t('widgets.widgetMenu.alignText.end'),
        selected: false,
        properties: { shortLabel: '', icon: 'align_text_end' },
    },
    {
        id: `${ALIGN_TEXT.JUSTIFY}`,
        label: i18n.t('widgets.widgetMenu.alignText.justify'),
        selected: false,
        properties: { shortLabel: '', icon: 'align_text_justify' },
    },
];

export const CUSTOM_ALIGN_TEXT_OPTION = (name: string) =>
    new SelectorPresetFormField(
        `${OPTIONS.ALIGN_TEXT}_${name}`,
        i18n.t(`widgets.widgetMenu.alignText.${name}`),
        [],
        SELECTOR_SHOW_AS.TOGGLE,
        0,
        '',
        ALIGN_TEXT_OPTIONS
    );

export const ABSOLUTE_PERCENTAGE_OPTIONS = [
    {
        id: `${ABSOLUTE_PERCENTAGE_VALUES.PERCENTAGE}`,
        label: i18n.t('widgets.widgetMenu.comparison.percentage'),
        selected: true,
        properties: { shortLabel: '', icon: '' },
    },
    {
        id: `${ABSOLUTE_PERCENTAGE_VALUES.ABSOLUTE}`,
        label: i18n.t('widgets.widgetMenu.comparison.absolute'),
        selected: false,
        properties: { shortLabel: '', icon: '' },
    },
];
